import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import LanguageSelector from "../components/LanguageSelector";
import { ArrowRight, CheckCircle, Compass, Battery, Brain, MessageCircle, Shield, Clock } from "lucide-react";

// WEDO Logo Component using the provided image
const WedoLogo = ({ variant = "color", className = "" }) => {
  const logoSrc = variant === "white" 
    ? "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/bdxr35h3_2024_10_make_it_matter.png"
    : "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/fb48vbve_2024_10_make_it_matter_bleu.png";
  
  return (
    <img 
      src={logoSrc} 
      alt="WEDO - Make it matter" 
      className={className}
    />
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <Compass className="w-6 h-6" />,
      titleKey: "landing.features.regulation",
      descKey: "landing.features.regulationDesc",
    },
    {
      icon: <Battery className="w-6 h-6" />,
      titleKey: "landing.features.energy",
      descKey: "landing.features.energyDesc",
    },
    {
      icon: <Brain className="w-6 h-6" />,
      titleKey: "landing.features.pcm",
      descKey: "landing.features.pcmDesc",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      titleKey: "landing.features.duration",
      descKey: "landing.features.durationDesc",
    },
  ];

  const modules = [
    { 
      number: 1, 
      titleKey: "landing.modules.module1", 
      descKey: "landing.modules.module1Desc",
      icon: <Compass className="w-5 h-5" />,
    },
    { 
      number: 2, 
      titleKey: "landing.modules.module2", 
      descKey: "landing.modules.module2Desc",
      icon: <Battery className="w-5 h-5" />,
    },
    { 
      number: 3, 
      titleKey: "landing.modules.module3", 
      descKey: "landing.modules.module3Desc",
      icon: <Brain className="w-5 h-5" />,
    },
    { 
      number: 4, 
      titleKey: "landing.modules.module4", 
      descKey: "landing.modules.module4Desc",
      icon: <MessageCircle className="w-5 h-5" />,
    },
    { 
      number: 5, 
      titleKey: "landing.modules.module5", 
      descKey: "landing.modules.module5Desc",
      icon: <Shield className="w-5 h-5" />,
    },
    { 
      number: 6, 
      titleKey: "landing.modules.module6", 
      descKey: "landing.modules.module6Desc",
      icon: <Clock className="w-5 h-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <WedoLogo className="h-10 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSelector variant="flagsOnly" className="text-slate-600" />
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
                className="text-slate-600 hover:text-[#101E4D]"
              >
                {t("landing.login")}
              </Button>
              <Button
                onClick={() => navigate("/start")}
                data-testid="header-start-btn"
                className="bg-[#101E4D] hover:bg-[#0A1330] text-white rounded-full px-6"
              >
                {t("landing.cta")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/l79ii873_degrad%C3%A9_wedo_make_it_matter.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo WEDO */}
            <div className="flex justify-center mb-10">
              <WedoLogo className="h-16 md:h-20 w-auto opacity-90" />
            </div>
            
            {/* FLOW Title - Grande typo avec effet */}
            <div className="mb-8">
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-[#101E4D] tracking-tight mb-3 animate-fade-in">
                FLOW
              </h1>
              <div className="w-24 h-1.5 bg-gradient-to-r from-[#FF6B35] via-[#7FD858] to-[#32A2FF] mx-auto rounded-full" />
            </div>
            
            {/* Sous-titre principal */}
            <p className="text-xl md:text-2xl lg:text-3xl text-slate-700 mb-6 animate-fade-in stagger-1 font-light">
              {t("landing.hero.program")} <span className="font-semibold text-[#101E4D]">{t("landing.hero.days")}</span>
              <br className="hidden sm:block" />
              <span className="block sm:inline"> {t("landing.hero.rhythm")}</span>
            </p>
            
            {/* Tagline - Style signature */}
            <div className="mb-8 animate-fade-in stagger-2">
              <p className="inline-flex items-center gap-3 text-lg md:text-xl text-[#101E4D] font-medium tracking-wider">
                <span className="w-8 h-px bg-[#101E4D]/30" />
                {t("landing.hero.tagline")}
                <span className="w-8 h-px bg-[#101E4D]/30" />
              </p>
            </div>
            
            {/* Description */}
            <div className="max-w-2xl mx-auto mb-10 animate-fade-in stagger-3">
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
                {t("landing.hero.description")}
                <br className="hidden md:block" />
                {t("landing.hero.description2")}
              </p>
              <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full" />
                {t("landing.hero.accompaniments")}
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in stagger-4">
              <Button
                onClick={() => navigate("/start")}
                data-testid="hero-start-btn"
                className="bg-[#101E4D] hover:bg-[#0A1330] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all group"
              >
                {t("landing.startFlow")}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById("modules").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-discover-btn"
                className="border-2 border-[#101E4D] text-[#101E4D] hover:bg-[#101E4D] hover:text-white rounded-full px-10 py-6 text-lg transition-all"
              >
                {t("landing.discoverModules")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <blockquote className="text-2xl md:text-3xl font-light text-[#101E4D] italic">
            {t("landing.quote1")}
          </blockquote>
        </div>
      </section>

      {/* What FLOW is / is not */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101E4D] mb-4">
              {t("landing.whatIs.title")}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What FLOW is NOT */}
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-500 mb-6">{t("landing.whatIs.isNot")}</h3>
              <ul className="space-y-3">
                {(t("landing.whatIs.notList", { returnObjects: true }) || []).map((item, index) => (
                  <li key={`not-${index}-${item.slice(0,15)}`} className="flex items-center text-slate-600">
                    <span className="w-2 h-2 bg-slate-300 rounded-full mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* What FLOW IS */}
            <div className="p-8 rounded-2xl bg-[#101E4D] text-white">
              <h3 className="text-xl font-semibold mb-6">{t("landing.whatIs.is")}</h3>
              <ul className="space-y-3">
                {(t("landing.whatIs.isList", { returnObjects: true }) || []).map((item, index) => (
                  <li key={`is-${index}-${item.slice(0,15)}`} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-[#7FD858] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101E4D] mb-4">
              {t("landing.features.title")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("landing.features.subtitle")}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div
                key={feature.titleKey}
                className="p-6 rounded-2xl bg-white hover:shadow-xl transition-all duration-300 border border-slate-100"
                data-testid={`feature-card-${feature.titleKey}`}
              >
                <div className="w-14 h-14 wedo-gradient rounded-xl flex items-center justify-center text-white mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#101E4D] mb-2">{t(feature.titleKey)}</h3>
                <p className="text-slate-600 text-sm">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101E4D] mb-4">
              {t("landing.howItWorks.title")}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#101E4D] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold text-[#101E4D] mb-3">{t("landing.howItWorks.step1Title")}</h3>
              <p className="text-slate-600 text-sm">
                {t("landing.howItWorks.step1Desc")}
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#101E4D] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold text-[#101E4D] mb-3">{t("landing.howItWorks.step2Title")}</h3>
              <p className="text-slate-600 text-sm">
                {t("landing.howItWorks.step2Desc")}
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#101E4D] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold text-[#101E4D] mb-3">{t("landing.howItWorks.step3Title")}</h3>
              <p className="text-slate-600 text-sm">
                {t("landing.howItWorks.step3Desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101E4D] mb-4">
              {t("landing.modules.title")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("landing.modules.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => (
              <div
                key={module.number}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                data-testid={`module-preview-${module.number}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#101E4D] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-lg">{module.number}</span>
                  </div>
                  <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                    {t("landing.modules.duration")}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-[#101E4D] mb-2">{t(module.titleKey)}</h3>
                <p className="text-slate-600 text-sm mb-4">{t(module.descKey)}</p>
                <div className="flex items-center text-xs text-slate-400">
                  {module.icon}
                  <span className="ml-2">{t("landing.modules.content")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section 2 */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <blockquote className="text-xl md:text-2xl font-light text-slate-600 italic">
            {t("landing.quote2")}
          </blockquote>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#101E4D] relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: "linear-gradient(135deg, #7FD858 0%, #FFEB3B 25%, #FF7043 50%, #EC407A 75%, #5C6BC0 100%)",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("landing.ctaSection.title")}
          </h2>
          <p className="text-xl text-slate-300 mb-4">
            {t("landing.ctaSection.tagline")}
          </p>
          <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
            {t("landing.ctaSection.description")}
          </p>
          <Button
            onClick={() => navigate("/start")}
            data-testid="cta-start-btn"
            className="bg-white hover:bg-slate-100 text-[#101E4D] rounded-full px-10 py-6 text-lg font-semibold shadow-lg"
          >
            {t("landing.startFlow")}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <WedoLogo className="h-8 w-auto" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <button
                onClick={() => navigate("/privacy")}
                className="text-slate-500 hover:text-[#101E4D] transition-colors"
              >
                {t("landing.footer.privacy")}
              </button>
              <button
                onClick={() => navigate("/terms")}
                className="text-slate-500 hover:text-[#101E4D] transition-colors"
              >
                {t("landing.footer.terms")}
              </button>
              <button
                onClick={() => navigate("/legal")}
                className="text-slate-500 hover:text-[#101E4D] transition-colors"
              >
                {t("landing.footer.legal")}
              </button>
            </div>
            <p className="text-slate-500 text-sm">
              {t("landing.footer.rights")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
